import React from "react";
import PropTypes from "prop-types";
import { ThemeProvider } from "@theconversation/ui";
import AmountDetails from "components/AmountDetails";
import ContactDetails from "components/ContactDetails";
import donationsTheme from "components/donationsTheme";
import FormError from "components/FormError";
import GiftAidSection from "components/GiftAidSection";
import Loading from "components/Loading";
import PaymentDetails from "components/PaymentDetails";
import PayPalManager from "lib/PayPalManager";
import RecaptchaDisclosure from "components/Recaptcha/Disclosure";
import RecaptchaValidation from "components/Recaptcha/Validation";
import StripeManager from "lib/StripeManager";

const DonationForm = ({
  address,
  amount,
  city,
  cardNumber,
  config,
  country,
  cvv,
  email,
  errors,
  expiry,
  fieldsActive,
  frequency,
  giftAid,
  lastName,
  loading,
  name,
  onChange,
  onSubmit,
  paymentProvider,
  payPalManager,
  postcode,
  publiclyRecogniseDonor,
  recaptcha,
  state,
  stripeManager,
  submitActive,
  title,
  url,
  valid,
}) => {
  const suggestedAmounts = config.suggestedAmounts[frequency];
  const giftAidSection = config.giftAid ?
    <GiftAidSection active={fieldsActive} giftAid={giftAid} onChange={onChange} /> : "";
  return (
    <ThemeProvider theme={donationsTheme}>
      <Loading show={loading} />

      <form
        acceptCharset="UTF-8"
        action={url}
        className="donation"
        id="new_donation"
        method="post"
        name="donation-form"
        noValidate="noValidate"
        onSubmit={onSubmit}
      >
        <FormError error={errors.payment_error} />
        <AmountDetails
          active={fieldsActive}
          amount={amount}
          currencySign={config.currencySign}
          errors={errors}
          frequency={frequency}
          initialFrequency={config.initialFrequency}
          onChange={onChange}
          defaultAmounts={config.defaultAmounts}
          suggestedAmounts={suggestedAmounts}
        />
        {giftAidSection}
        <ContactDetails
          active={fieldsActive}
          address={address}
          city={city}
          country={country}
          email={email}
          errors={errors}
          homeCountryCode={config.homeCountryCode}
          lastName={lastName}
          name={name}
          onChange={onChange}
          postcode={postcode}
          publiclyRecogniseDonor={publiclyRecogniseDonor}
          showAddressInputs={config.showAddressInputs}
          showOptionalAddress={config.showOptionalAddress}
          state={state}
          title={title}
        />
        <PaymentDetails
          amount={amount}
          cardNumber={cardNumber}
          cvv={cvv}
          errors={errors}
          expiry={expiry}
          frequency={frequency}
          fieldsActive={fieldsActive}
          locale={config.locale}
          onChange={onChange}
          paymentProvider={paymentProvider}
          payPalManager={payPalManager}
          stripeManager={stripeManager}
          submitActive={submitActive}
          valid={valid}
        />
        <RecaptchaValidation
          enabled={recaptcha.enabled}
          instance={recaptcha.instance}
          locale={config.locale}
          onVerify={recaptcha.onVerify}
          siteKey={recaptcha.siteKey}
        />
        <RecaptchaDisclosure enabled={recaptcha.enabled} />
      </form>
    </ThemeProvider>
  );
};

export const ConfigDefaultProps = {
  defaultAmounts: { once: 10, monthly: 10 },
  giftAid: false,
  locale: "en",
  suggestedAmounts: { once: ["10"], monthly: ["10"] },
};

DonationForm.defaultProps = {
  address: undefined,
  amount: undefined,
  city: undefined,
  country: undefined,
  email: undefined,
  cardNumber: false,
  config: ConfigDefaultProps,
  cvv: false,
  errors: {},
  expiry: false,
  fieldsActive: true,
  frequency: "once",
  giftAid: false,
  lastName: undefined,
  loading: false,
  name: undefined,
  onChange: () => {},
  onSubmit: () => {},
  paymentProvider: "credit_card",
  payPalManager: new PayPalManager({ }),
  postcode: undefined,
  publiclyRecogniseDonor: false,
  recaptcha: {
    enabled: false,
    instance: { current: null },
    onVerify: () => {},
    siteKey: "site-key",
  },
  state: undefined,
  stripeManager: new StripeManager({ }),
  submitActive: true,
  title: undefined,
  url: undefined,
  valid: undefined,
};

DonationForm.propTypes = {
  address: PropTypes.string,
  amount: PropTypes.string,
  city: PropTypes.string,
  cardNumber: PropTypes.bool,
  config: PropTypes.shape({
    currencySign: PropTypes.string,
    defaultAmounts: PropTypes.shape({
      once: PropTypes.string,
      monthly: PropTypes.string,
    }),
    giftAid: PropTypes.bool,
    initialFrequency: PropTypes.oneOf(["once", "monthly"]),
    locale: PropTypes.string,
    homeCountryCode: PropTypes.string,
    showAddressInputs: PropTypes.bool,
    showOptionalAddress: PropTypes.bool,
    suggestedAmounts: PropTypes.shape({
      once: PropTypes.arrayOf(PropTypes.string),
      monthly: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  country: PropTypes.string,
  cvv: PropTypes.bool,
  email: PropTypes.string,
  errors: PropTypes.shape({
    address: PropTypes.string,
    amount: PropTypes.string,
    cardNumber: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    cvv: PropTypes.string,
    email: PropTypes.string,
    expiry: PropTypes.string,
    frequency: PropTypes.string,
    giftAid: PropTypes.string,
    lastName: PropTypes.string,
    name: PropTypes.string,
    payment_error: PropTypes.string,
    paypal: PropTypes.string,
    postcode: PropTypes.string,
    state: PropTypes.string,
    title: PropTypes.string,
  }),
  expiry: PropTypes.bool,
  fieldsActive: PropTypes.bool,
  frequency: PropTypes.oneOf(["once", "monthly"]),
  giftAid: PropTypes.bool,
  lastName: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  paymentProvider: PropTypes.oneOf(["credit_card", "paypal"]),
  payPalManager: PropTypes.instanceOf(PayPalManager),
  postcode: PropTypes.string,
  publiclyRecogniseDonor: PropTypes.bool,
  recaptcha: PropTypes.shape({
    enabled: PropTypes.bool,
    instance: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.elementType }),
    ]),
    onVerify: PropTypes.func,
    siteKey: PropTypes.string,
  }),
  state: PropTypes.string,
  stripeManager: PropTypes.instanceOf(StripeManager),
  submitActive: PropTypes.bool,
  title: PropTypes.string,
  url: PropTypes.string,
  valid: PropTypes.bool,
};

export default DonationForm;
